import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container, Box, Paper, Typography, TextField, Button, Select, MenuItem,
  FormControl, InputLabel, Table, TableHead, TableBody, TableRow, TableCell,
  IconButton, Alert, CircularProgress, useMediaQuery, useTheme, Chip, ListItemButton, Stack, InputAdornment,
  ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import { Save, Delete, Icecream, PointOfSale, Add, Remove, Search } from '@mui/icons-material';
import { MenuInicial } from './MenuInicial';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  API_BASE_URL, REGISTRO_FORMDATA, REGISTRO_ALTA, REGISTRO_GET, REGISTRO_ACTUALIZAR, TURNO_ACTUAL, FACTURACION_GET
} from '../services/apiConfig';
import TituloPagina from './utils/TituloPagina.jsx';
import VolverBreadcrumb from './utils/VolverBreadcrumb.jsx';
import Cargando from './utils/Cargando.jsx';
import TildeExito from './utils/TildeExito.jsx';
import axios from 'axios';
import { imprimirTicketSilencioso } from '../services/impresora';
import { reproducirSonidoExito } from '../services/sonido';
import { avisarExito, avisarAdvertencia } from '../services/toast';
import { registrarLogError } from '../services/api';
import { leerProductosCache, guardarProductosCache } from '../services/productosCache';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './DatePickerStyles.css';

const authHeader = () => {
  const t = sessionStorage.getItem('authToken');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const fmtMoneda = (n) => `$${Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const round2 = (n) => Math.round(Number(n || 0) * 100) / 100;

// La API ya resuelve la ruta relativa (imagen_url) y verifica que el archivo
// exista; acá solo le anteponemos la base. Si no viene, cae al heladito.
function ProductoImagen({ imagenUrl: rel, size = 60 }) {
  const [err, setErr] = useState(false);
  const url = rel && !err ? `${API_BASE_URL}${rel}` : null;
  return (
    <Box sx={{ width: size, height: size, flexShrink: 0, borderRadius: 1, bgcolor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
      {url
        ? <img src={url} alt="" onError={() => setErr(true)} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        : <Icecream sx={{ fontSize: Math.round(size * 0.55), color: 'primary.main', opacity: 0.65 }} />}
    </Box>
  );
}

function RegistroProducto() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [params] = useSearchParams();
  const id = params.get('id');
  const modo = id ? 'editar' : 'crear';

  const userData = JSON.parse(sessionStorage.getItem('userData') || '{}');

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [mostrarExito, setMostrarExito] = useState(false);
  const [error, setError] = useState('');

  // Cabecera
  const [fecha, setFecha] = useState(new Date());
  const [sucursalId, setSucursalId] = useState('');
  const [observaciones, setObservaciones] = useState('');

  // Turno de caja: solo se exige para CARGAR una venta nueva (no para editar
  // una ya existente). Mapa sucursal_id -> turno abierto (o null si no hay).
  const [turnosPorSucursal, setTurnosPorSucursal] = useState({});

  // Catálogos
  const [productos, setProductos] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [grupoSel, setGrupoSel] = useState(null);
  const [busquedaProd, setBusquedaProd] = useState('');

  // Detalle
  const [items, setItems] = useState([]);
  const [pagaCon, setPagaCon] = useState('');

  // Descuento sobre toda la venta (no por ítem). El monto realmente
  // descontado lo recalcula y valida el servidor; acá se refleja el mismo
  // cálculo solo para que la vista previa no difiera de lo que se termina guardando.
  const [descuentoTipo, setDescuentoTipo] = useState('ninguno'); // 'ninguno' | 'porcentaje' | 'fijo'
  const [descuentoValor, setDescuentoValor] = useState('');

  // Solo el certificado QZ Tray es global (firma de impresión) — nombre de
  // fantasía y pie de ticket son por sucursal, vienen en sucursalActual.
  const [impresionGlobal, setImpresionGlobal] = useState({});

  useEffect(() => {
    axios.get(API_BASE_URL + FACTURACION_GET, { headers: authHeader() })
      .then((res) => {
        const cfg = res.data?.data ?? {};
        setImpresionGlobal({ impresion_qz_cert: cfg.impresion_qz_cert || '' });
      })
      .catch(() => { /* sin config global: se sigue sin imprimir */ });
  }, []);

  const subtotalBruto = items.reduce((acc, it) => acc + Number(it.subtotal || 0), 0);
  const descuentoMonto = (() => {
    const v = Number(descuentoValor) || 0;
    if (descuentoTipo === 'porcentaje') return round2(subtotalBruto * Math.min(Math.max(v, 0), 100) / 100);
    if (descuentoTipo === 'fijo') return round2(Math.min(Math.max(v, 0), subtotalBruto));
    return 0;
  })();
  const total = Math.max(0, round2(subtotalBruto - descuentoMonto));
  const unidades = items.reduce((acc, it) => acc + Number(it.cantidad || 0), 0);
  // Peso total vendido: 1 (kilo) = 1.000 en art_peso, sin conversión de unidades.
  const pesoTotal = items.reduce((acc, it) => acc + Number(it.cantidad || 0) * Number(it.peso || 0), 0);
  const qtyMap = useMemo(() => {
    const m = {};
    items.forEach((it) => { m[it.id_club_grido] = it.cantidad; });
    return m;
  }, [items]);

  const grupos = useMemo(() => {
    const m = new Map();
    productos.forEach((p) => {
      if (!m.has(p.grupo)) m.set(p.grupo, { nombre: p.grudescrip || `Grupo ${p.grupo}`, orden: Number(p.gruorden ?? 0) });
    });
    return Array.from(m, ([grupo, v]) => ({ grupo, nombre: v.nombre, orden: v.orden }))
      .sort((a, b) => a.orden - b.orden);
  }, [productos]);

  // Con búsqueda: filtra por nombre/artículo en TODOS los grupos. Sin búsqueda:
  // muestra los del grupo seleccionado.
  const productosMostrados = useMemo(() => {
    const t = busquedaProd.trim().toLowerCase();
    if (t) {
      return productos.filter((p) => String(p.art_descripcion || '').toLowerCase().includes(t));
    }
    return productos.filter((p) => p.grupo === grupoSel);
  }, [productos, grupoSel, busquedaProd]);

  // Trae el catálogo: usa la caché de productos si existe (evita repetir la
  // consulta pesada); sucursales/última venta siempre se piden frescas (son
  // livianas y determinan a qué sucursal se puede vender).
  const cargarCatalogo = useCallback(async (forzar = false) => {
    const cache = !forzar ? leerProductosCache() : null;
    if (cache) {
      const fd = await axios.get(API_BASE_URL + REGISTRO_FORMDATA, { headers: authHeader(), params: { solo_sucursales: 1 } });
      const data = fd.data?.data ?? {};
      return { productos: cache, sucursales: data.sucursales ?? [], ultima_sucursal_id: data.ultima_sucursal_id ?? null };
    }
    const fd = await axios.get(API_BASE_URL + REGISTRO_FORMDATA, { headers: authHeader() });
    const data = fd.data?.data ?? {};
    const prods = data.productos ?? [];
    guardarProductosCache(prods);
    return { productos: prods, sucursales: data.sucursales ?? [], ultima_sucursal_id: data.ultima_sucursal_id ?? null };
  }, []);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const { productos: prods, sucursales: sucs, ultima_sucursal_id: ultima } = await cargarCatalogo();
      setProductos(prods);
      setSucursales(sucs);
      if (prods.length) setGrupoSel(prods[0].grupo);

      if (id) {
        const res = await axios.get(API_BASE_URL + REGISTRO_GET, { params: { id }, headers: authHeader() });
        const r = res.data?.data;
        if (r) {
          setFecha(r.fecha ? new Date(String(r.fecha).replace(' ', 'T')) : new Date());
          setSucursalId(r.sucursal_id ?? '');
          setObservaciones(r.observaciones ?? '');
          setDescuentoTipo(r.descuento_tipo && r.descuento_tipo !== 'ninguno' ? r.descuento_tipo : 'ninguno');
          setDescuentoValor(r.descuento_tipo && r.descuento_tipo !== 'ninguno' ? String(r.descuento_valor ?? '') : '');
          setItems((r.items ?? []).map((it) => ({
            id_club_grido: it.id_club_grido,
            descripcion: it.descripcion ?? it.art_descripcion ?? '',
            cantidad: Number(it.cantidad),
            precio_unitario: Number(it.precio_unitario),
            subtotal: Number(it.subtotal),
            peso: Number(it.art_peso || 0),
          })));
        }
      } else if (sucs.length) {
        // Averiguar en cuáles de las sucursales asignadas hay un turno abierto
        // AHORA MISMO, para no dejar elegir una donde no se puede vender:
        // - 1 sola con turno abierto -> se elige automáticamente (sin preguntar).
        // - 2 o más con turno abierto -> hay que elegir a cuál cargar la venta.
        // - ninguna -> se bloquea (pantalla de "abrir turno").
        const resultados = await Promise.all(
          sucs.map((s) => axios.get(API_BASE_URL + TURNO_ACTUAL, { params: { sucursal_id: s.id }, headers: authHeader() })
            .then((r) => r.data?.data ?? null)
            .catch(() => null))
        );
        const mapa = {};
        sucs.forEach((s, i) => { mapa[s.id] = resultados[i]; });
        setTurnosPorSucursal(mapa);

        const conTurno = sucs.filter((s) => mapa[s.id]);
        if (conTurno.length === 1) {
          setSucursalId(Number(conTurno[0].id));
        } else if (conTurno.length > 1) {
          // Default: la sucursal de la última venta (si tiene turno abierto); si no, la primera con turno.
          const sidUltima = ultima != null && conTurno.some((s) => Number(s.id) === Number(ultima)) ? Number(ultima) : null;
          setSucursalId(sidUltima ?? Number(conTurno[0].id));
        } else {
          // Ninguna tiene turno abierto: dejamos la primera asignada para mostrar el bloqueo.
          setSucursalId(Number(sucs[0].id));
        }
      }
    } catch (e) {
      setError(e.response?.data?.message || 'Error al cargar datos.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const actualizarCatalogo = async () => {
    try {
      const { productos: prods } = await cargarCatalogo(true);
      setProductos(prods);
    } catch { /* silencioso: seguimos con lo que ya había en pantalla */ }
  };

  useEffect(() => { cargar(); }, [cargar]);

  // Turno abierto de la sucursal seleccionada (ya lo trajimos todo en cargar()).
  // undefined en modo edición: ahí no aplica el bloqueo por turno.
  const turnoActual = modo === 'crear' ? (turnosPorSucursal[sucursalId] ?? null) : undefined;

  // En modo alta, solo se puede elegir entre sucursales con turno abierto
  // (si ninguna tiene, mostramos todas igual para que el usuario vea cuál es).
  // En modo editar la sucursal ya viene fija del registro cargado — nunca se
  // puede cambiar acá, solo los productos, así que no hay nada para elegir.
  const sucursalesParaElegir = useMemo(() => {
    if (modo === 'editar') return [];
    const conTurno = sucursales.filter((s) => turnosPorSucursal[s.id]);
    return conTurno.length > 0 ? conTurno : sucursales;
  }, [modo, sucursales, turnosPorSucursal]);

  const agregarProducto = (prod) => {
    setError('');
    // El producto vendido se identifica por id_club_grido (el real del
    // sistema Grido); sin ese dato no se puede vincular la venta al producto.
    if (!prod.id_club_grido) {
      setError(`"${prod.art_descripcion}" no tiene ID Club Grido cargado. Completalo en el catálogo para poder venderlo.`);
      return;
    }
    setItems((prev) => {
      const i = prev.findIndex((x) => x.id_club_grido === prod.id_club_grido);
      if (i >= 0) {
        const copy = [...prev];
        const cant = copy[i].cantidad + 1;
        copy[i] = { ...copy[i], cantidad: cant, subtotal: round2(cant * copy[i].precio_unitario) };
        return copy;
      }
      const precio = Number(prod.art_precio_normal || 0);
      return [...prev, {
        id_club_grido: prod.id_club_grido,
        descripcion: prod.art_descripcion,
        cantidad: 1,
        precio_unitario: precio,
        subtotal: round2(precio),
        peso: Number(prod.art_peso || 0),
      }];
    });
  };

  // Resta una unidad desde el tile; si llega a 0, saca el producto del detalle.
  const decrementarProducto = (prod) => {
    setItems((prev) => {
      const i = prev.findIndex((x) => x.id_club_grido === prod.id_club_grido);
      if (i < 0) return prev;
      const cant = prev[i].cantidad - 1;
      if (cant <= 0) return prev.filter((_, idx) => idx !== i);
      const copy = [...prev];
      copy[i] = { ...copy[i], cantidad: cant, subtotal: round2(cant * copy[i].precio_unitario) };
      return copy;
    });
  };

  const setCantidad = (idx, val) => {
    const c = Math.max(1, parseInt(val || 1, 10) || 1);
    setItems((prev) => prev.map((it, i) => i === idx ? { ...it, cantidad: c, subtotal: round2(c * it.precio_unitario) } : it));
  };
  const setPrecioItem = (idx, val) => {
    const p = Math.max(0, parseFloat(val) || 0);
    setItems((prev) => prev.map((it, i) => i === idx ? { ...it, precio_unitario: p, subtotal: round2(it.cantidad * p) } : it));
  };
  const quitarItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));

  const guardar = async () => {
    setError('');
    if (!sucursalId) { setError('Elegí un local.'); return; }
    if (items.length === 0) { setError('Agregá al menos un producto.'); return; }
    setSubmitting(true);
    const payload = {
      fecha: `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, '0')}-${String(fecha.getDate()).padStart(2, '0')} ${String(fecha.getHours()).padStart(2, '0')}:${String(fecha.getMinutes()).padStart(2, '0')}:00`,
      sucursal_id: sucursalId,
      observaciones,
      items,
      descuento_tipo: descuentoTipo,
      descuento_valor: Number(descuentoValor) || 0,
    };
    try {
      if (modo === 'editar') {
        const resActualizar = await axios.post(API_BASE_URL + REGISTRO_ACTUALIZAR, { id: Number(id), ...payload }, { headers: authHeader() });
        if (!resActualizar.data?.success) throw new Error(resActualizar.data?.message || 'Error al guardar.');
        avisarExito('Registro actualizado.');
        // Edición de un registro puntual: sí tiene sentido volver al listado.
        setTimeout(() => navigate('/registros-productos'), 900);
        return;
      }

      const resAlta = await axios.post(API_BASE_URL + REGISTRO_ALTA, payload, { headers: authHeader() });
      if (!resAlta.data?.success) throw new Error(resAlta.data?.message || 'Error al guardar.');
      // Impresión de comanda: best-effort, nunca frena el flujo (no se espera
      // el resultado antes de mostrar la confirmación).
      const cambio = pagaCon !== '' ? Number(pagaCon) - total : null;
      imprimirTicketSilencioso(
        {
          id: resAlta.data?.data?.id, fecha, sucursalNombre: sucursalActual?.nombre, sucursalDireccion: sucursalActual?.direccion,
          vendedor: userData.username, items, subtotal: subtotalBruto, total, observaciones,
          pagaCon: pagaCon !== '' ? pagaCon : null, cambio,
          descuentoTipo, descuentoValor: Number(descuentoValor) || 0, descuentoMonto,
        },
        sucursalActual,
        impresionGlobal,
      ).then((r) => {
        // Solo se intenta imprimir si la sucursal tiene la comandera habilitada
        // (imprimirTicketSilencioso corta antes si no lo está: motivo 'sin_configurar').
        if (!r.ok && r.motivo !== 'sin_configurar') {
          avisarAdvertencia('Registro guardado. No se pudo imprimir la comanda.');
          registrarLogError(userData.usuarioid, 'ImpresionComanda', `Sucursal ${sucursalActual?.nombre ?? sucursalId}: ${r.motivo}`);
        }
      });

      // Alta: en vez de navegar (recargaría catálogo/turno de nuevo), se
      // muestra el tilde de éxito y se reinicia el formulario en la misma
      // pantalla — el cajero queda listo para registrar la próxima venta.
      setMostrarExito(true);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Error al guardar.');
      setSubmitting(false);
    }
  };

  // Se llama cuando el tilde de éxito termina de mostrarse (ver TildeExito):
  // recién ahí se limpia el formulario, para no cortar la animación a mitad.
  const finalizarExito = () => {
    setMostrarExito(false);
    setSubmitting(false);
    setItems([]);
    setObservaciones('');
    setPagaCon('');
    setFecha(new Date());
    setDescuentoTipo('ninguno');
    setDescuentoValor('');
  };

  if (loading) {
    return (
      <Container maxWidth={isMobile ? false : 'xl'} sx={{ px: isMobile ? 1 : 3 }}>
        <MenuInicial />
        <Cargando />
      </Container>
    );
  }

  // Sin sucursal asignada: no se puede vender (ni en alta ni en edición).
  if (!error && sucursales.length === 0) {
    return (
      <Container maxWidth={isMobile ? false : 'xl'} sx={{ px: isMobile ? 1 : 3 }}>
        <MenuInicial />
        <Box sx={{ my: 3 }}>
          <TituloPagina icon={PointOfSale} titulo="Registrar" subtitulo="Registro de productos" />
          <VolverBreadcrumb to="/registros-productos" label="Registros de productos" />
          <Alert severity="warning">
            No tenés una sucursal asignada. Pedile a un administrador o franquiciado que te asigne una para poder registrar.
          </Alert>
        </Box>
      </Container>
    );
  }

  // Sin turno abierto no se puede cargar una venta nueva (sí se puede editar
  // una existente, que no depende del turno actual).
  if (modo === 'crear' && sucursalId && turnoActual === null) {
    const ningunaConTurno = sucursales.every((s) => !turnosPorSucursal[s.id]);
    const sucNombre = sucursales.find((s) => Number(s.id) === Number(sucursalId))?.nombre;
    return (
      <Container maxWidth={isMobile ? false : 'xl'} sx={{ px: isMobile ? 1 : 3 }}>
        <MenuInicial />
        <Box sx={{ my: 3 }}>
          <TituloPagina icon={PointOfSale} titulo="Registrar" subtitulo="Registro de productos" />
          <VolverBreadcrumb to="/registros-productos" label="Registros de productos" />
          <Alert severity="warning" sx={{ mb: 2 }}>
            {ningunaConTurno && sucursales.length > 1
              ? 'No hay ningún turno de caja abierto en tus sucursales. Abrí uno para poder registrar.'
              : `No hay un turno de caja abierto en ${sucNombre || 'esta sucursal'}. Abrí un turno para poder registrar.`}
          </Alert>
          <Button
            variant="contained" color="primary"
            onClick={() => navigate(ningunaConTurno && sucursales.length > 1 ? '/turno' : `/turno?sucursal_id=${sucursalId}`)}
          >
            Abrir turno
          </Button>
        </Box>
      </Container>
    );
  }

  const sucursalActual = sucursales.find((s) => Number(s.id) === Number(sucursalId));

  // --- Panel de grupos ---
  const panelGrupos = isMobile ? (
    <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 1, mb: 1, '::-webkit-scrollbar': { height: 6 } }}>
      {grupos.map((g) => (
        <Chip key={g.grupo} label={g.nombre} color={g.grupo === grupoSel ? 'primary' : 'default'}
          variant={g.grupo === grupoSel ? 'filled' : 'outlined'} onClick={() => { setGrupoSel(g.grupo); setBusquedaProd(""); }}
          sx={{ flexShrink: 0, fontWeight: 600 }} />
      ))}
    </Box>
  ) : (
    <Paper variant="outlined" sx={{ width: 200, flexShrink: 0, maxHeight: 470, overflow: 'auto' }}>
      {grupos.map((g) => (
        <ListItemButton key={g.grupo} selected={g.grupo === grupoSel} onClick={() => { setGrupoSel(g.grupo); setBusquedaProd(""); }}
          sx={{ py: 1.2, borderLeft: g.grupo === grupoSel ? '3px solid' : '3px solid transparent', borderColor: g.grupo === grupoSel ? 'primary.main' : 'transparent' }}>
          <Typography variant="body2" sx={{ fontWeight: g.grupo === grupoSel ? 700 : 400 }}>{g.nombre}</Typography>
        </ListItemButton>
      ))}
    </Paper>
  );

  // --- Grilla de productos (tiles POS) ---
  // En desktop las tarjetas van más grandes (más fáciles de tocar/leer);
  // en mobile/tablet se mantiene el tamaño compacto de siempre.
  const tileMin = isMobile ? 160 : 260;
  const tileImgSize = isMobile ? 40 : 65;
  const tileTextVariant = isMobile ? 'caption' : 'body1';
  const panelProductos = (
    <Box sx={{
      flex: 1, display: 'grid',
      // Auto-ajustable: se adapta al ancho real disponible (mobile, tablet o
      // desktop, pineado o no) sin saltos fijos por breakpoint.
      gridTemplateColumns: `repeat(auto-fill, minmax(${tileMin}px, 1fr))`,
      gap: isMobile ? 1 : 1.5, alignContent: 'start',
    }}>
      {productosMostrados.map((p) => {
        const q = qtyMap[p.id_club_grido] || 0;
        return (
          <Paper key={p.id} variant="outlined" onClick={() => agregarProducto(p)}
            sx={{
              position: 'relative', p: isMobile ? 0.75 : 1.5, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: isMobile ? 1 : 1.5,
              height: '100%', transition: 'all .12s', userSelect: 'none',
              borderColor: q > 0 ? 'primary.main' : 'divider',
              '&:hover': { borderColor: 'primary.main', boxShadow: 2 },
              '&:active': { transform: 'scale(0.98)' },
            }}>
            <ProductoImagen imagenUrl={p.imagen_url} size={tileImgSize} />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant={tileTextVariant} sx={{ fontWeight: 600, lineHeight: 1.1, display: 'block' }}>{p.art_descripcion}</Typography>
              <Typography variant={tileTextVariant} color="primary" sx={{ fontWeight: 700 }}>{fmtMoneda(p.art_precio_normal)}</Typography>
              {Number(p.art_peso) > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1 }}>
                  {Number(p.art_peso).toFixed(3)} kg
                </Typography>
              )}
            </Box>
            {q > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                <IconButton color="error"
                  onClick={(e) => { e.stopPropagation(); decrementarProducto(p); }}
                  sx={{ width: 30, height: 30, border: '1px solid', borderColor: 'error.light', '&:hover': { bgcolor: 'error.light', color: '#fff' } }}>
                  <Remove sx={{ fontSize: 18 }} />
                </IconButton>
                <Typography sx={{ fontWeight: 800, minWidth: 18, textAlign: 'center', color: 'primary.main' }}>{q}</Typography>
              </Box>
            )}
          </Paper>
        );
      })}
      {productosMostrados.length === 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ p: 1, gridColumn: '1 / -1' }}>Sin productos en este grupo.</Typography>
      )}
    </Box>
  );

  // --- Stepper de cantidad reutilizable ---
  const Stepper = ({ it, idx }) => (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <IconButton size="small" onClick={() => setCantidad(idx, it.cantidad - 1)} sx={{ border: '1px solid', borderColor: 'divider' }}><Remove fontSize="inherit" /></IconButton>
      <TextField value={it.cantidad} onChange={(e) => setCantidad(idx, e.target.value)} type="number" size="small"
        inputProps={{ min: 1, style: { textAlign: 'center', width: 34, padding: '4px 0' } }} variant="standard" />
      <IconButton size="small" onClick={() => setCantidad(idx, it.cantidad + 1)} sx={{ border: '1px solid', borderColor: 'divider' }}><Add fontSize="inherit" /></IconButton>
    </Stack>
  );

  // --- Detalle (cart) ---
  const detalle = items.length === 0 ? (
    <Box sx={{ textAlign: 'center', color: 'text.secondary', py: 4 }}>
      <Icecream sx={{ fontSize: 40, opacity: 0.3 }} />
      <Typography variant="body2" sx={{ mt: 1 }}>Tocá un producto para agregarlo al registro.</Typography>
    </Box>
  ) : isMobile ? (
    <Stack spacing={1}>
      {items.map((it, idx) => (
        <Paper key={idx} variant="outlined" sx={{ p: 1.2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{it.descripcion}</Typography>
            <IconButton size="small" color="error" onClick={() => quitarItem(idx)} sx={{ mt: -0.5, mr: -0.5 }}><Delete fontSize="small" /></IconButton>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
            <Stepper it={it} idx={idx} />
            <Box sx={{ textAlign: 'right' }}>
              <TextField value={it.precio_unitario} onChange={(e) => setPrecioItem(idx, e.target.value)} type="number" size="small" variant="standard"
                inputProps={{ min: 0, step: '0.01', style: { textAlign: 'right', width: 70 } }} label="Precio" />
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{fmtMoneda(it.subtotal)}</Typography>
              {Number(it.peso) > 0 && (
                <Typography variant="caption" color="text.secondary" display="block">
                  {(Number(it.cantidad) * Number(it.peso)).toFixed(3)} kg
                </Typography>
              )}
            </Box>
          </Box>
        </Paper>
      ))}
    </Stack>
  ) : (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ minWidth: 620 }}>
        <TableHead>
          <TableRow>
            <TableCell>Producto</TableCell>
            <TableCell align="center" width={140}>Cantidad</TableCell>
            <TableCell align="right" width={120}>Precio</TableCell>
            <TableCell align="right" width={90}>Peso</TableCell>
            <TableCell align="right">Subtotal</TableCell>
            <TableCell align="right" width={40}></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((it, idx) => (
            <TableRow key={idx} hover>
              <TableCell>{it.descripcion}</TableCell>
              <TableCell align="center"><Box sx={{ display: 'flex', justifyContent: 'center' }}><Stepper it={it} idx={idx} /></Box></TableCell>
              <TableCell align="right">
                <TextField value={it.precio_unitario} onChange={(e) => setPrecioItem(idx, e.target.value)} type="number" size="small" variant="standard"
                  inputProps={{ min: 0, step: '0.01', style: { textAlign: 'right', width: 80 } }} />
              </TableCell>
              <TableCell align="right">{Number(it.peso) > 0 ? `${(Number(it.cantidad) * Number(it.peso)).toFixed(3)} kg` : '—'}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>{fmtMoneda(it.subtotal)}</TableCell>
              <TableCell align="right"><IconButton size="small" color="error" onClick={() => quitarItem(idx)}><Delete fontSize="small" /></IconButton></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );

  return (
    <Container maxWidth={isMobile ? false : 'xl'} sx={{ px: isMobile ? 1 : 3 }}>
      <TildeExito submitting={submitting} mostrar={mostrarExito} onFinalizado={finalizarExito} sonido={reproducirSonidoExito} />
      <MenuInicial />
      <Box sx={{ my: 3, pb: { xs: '150px', md: '96px' } }}>
        <TituloPagina
          icon={PointOfSale}
          titulo={modo === 'editar' ? `Editar registro #${id}` : 'Registrar'}
          subtitulo="Registro de productos"
          ayuda="Elegí productos tocando cada tarjeta para armar el registro. Necesitás un turno de caja abierto en la sucursal para poder guardarlo."
        />
        <VolverBreadcrumb to="/registros-productos" label="Registros de productos" />

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Cabecera compacta: una sola línea siempre (Vendedor/Local pasan a
            texto simple cuando no son editables), para no restarle altura a
            la grilla de productos de abajo. */}
        <Paper variant="outlined" sx={{ px: 1.5, py: 1, mb: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ width: 175, flexShrink: 0 }}>
              <DatePicker
                selected={fecha} onChange={setFecha} showTimeSelect timeFormat="HH:mm" timeIntervals={5}
                timeCaption="Hora" dateFormat="dd/MM/yyyy HH:mm"
                customInput={<TextField label="Fecha y hora" size="small" fullWidth />}
              />
            </Box>

            {sucursalesParaElegir.length > 1 ? (
              <FormControl size="small" sx={{ width: 170, flexShrink: 0 }}>
                <InputLabel>Local</InputLabel>
                <Select label="Local" value={sucursalId} onChange={(e) => setSucursalId(Number(e.target.value))}>
                  {sucursalesParaElegir.map((s) => <MenuItem key={s.id} value={s.id}>{s.nombre}</MenuItem>)}
                </Select>
              </FormControl>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
                Local: <strong>{sucursalActual?.nombre ?? '—'}</strong>
              </Typography>
            )}

            <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
              Vendedor: <strong>{userData.username || '—'}</strong>
            </Typography>

            <TextField
              size="small" label="Observaciones" value={observaciones}
              onChange={(e) => setObservaciones(e.target.value)}
              sx={{ flex: 1, minWidth: 160 }}
            />
          </Box>

          {sucursalesParaElegir.length > 1 && (
            <Alert severity="warning" sx={{ py: 0, mt: 1 }}>
              {modo === 'crear' ? 'Tenés más de una sucursal con turno abierto' : 'Tenés más de una sucursal'} — estás cargando para: <strong>{sucursalActual?.nombre ?? '—'}</strong>
            </Alert>
          )}
        </Paper>

        {/* Selector visual */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
            <TextField
              fullWidth size="small" placeholder="Buscar producto por nombre o artículo…"
              value={busquedaProd} onChange={(e) => setBusquedaProd(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>,
                endAdornment: busquedaProd
                  ? <InputAdornment position="end"><Button size="small" onClick={() => setBusquedaProd('')} sx={{ minWidth: 0 }}>✕</Button></InputAdornment>
                  : null,
              }}
            />
            <Button size="small" variant="outlined" onClick={actualizarCatalogo} sx={{ flexShrink: 0, whiteSpace: 'nowrap' }} title="Volver a traer productos, fotos y precios del servidor">
              Actualizar catálogo
            </Button>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
            {busquedaProd.trim() === '' && panelGrupos}
            {panelProductos}
          </Box>
        </Paper>

        {/* Detalle */}
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Detalle {items.length > 0 && <Chip size="small" label={`${items.length} ítem${items.length !== 1 ? 's' : ''}`} sx={{ ml: 0.5 }} />}
          </Typography>
          {detalle}

          {items.length > 0 && (
            <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid', borderColor: 'divider', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
              <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>Descuento</Typography>
              <ToggleButtonGroup
                size="small" exclusive value={descuentoTipo}
                onChange={(e, val) => { if (val) { setDescuentoTipo(val); setDescuentoValor(''); } }}
              >
                <ToggleButton value="ninguno">Sin desc.</ToggleButton>
                <ToggleButton value="porcentaje">%</ToggleButton>
                <ToggleButton value="fijo">$</ToggleButton>
              </ToggleButtonGroup>
              {descuentoTipo !== 'ninguno' && (
                <TextField
                  size="small" type="number" label={descuentoTipo === 'porcentaje' ? '%' : '$'}
                  value={descuentoValor} onChange={(e) => setDescuentoValor(e.target.value)}
                  inputProps={{ min: 0, step: '0.01', inputMode: 'decimal' }}
                  sx={{ width: 110 }}
                />
              )}
              {descuentoMonto > 0 && (
                <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 600 }}>
                  -{fmtMoneda(descuentoMonto)}
                </Typography>
              )}
            </Box>
          )}

          {items.length > 0 && (() => {
            const cambio = pagaCon !== '' ? Number(pagaCon) - total : null;
            return (
              <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 2 }}>
                <TextField
                  size="small" type="number" label="Ingresa"
                  value={pagaCon} onChange={(e) => setPagaCon(e.target.value)}
                  inputProps={{ min: 0, step: '0.01', inputMode: 'decimal' }}
                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                  sx={{ flex: 1, maxWidth: 220 }}
                />
                <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1 }}>Cambio</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: cambio == null ? 'text.primary' : (cambio < 0 ? 'error.main' : 'success.main') }}>
                    {cambio == null ? '—' : fmtMoneda(cambio)}
                  </Typography>
                </Box>
              </Box>
            );
          })()}
        </Paper>
      </Box>

      {/* Barra fija total + guardar */}
      <Paper elevation={8} square sx={{
        position: 'fixed', left: 0, right: 0, bottom: { xs: '65px', md: 0 }, zIndex: 900,
        borderTop: '1px solid', borderColor: 'divider',
      }}>
        <Container maxWidth="xl" sx={{
          py: 1.25, px: { xs: 2, md: 3 }, display: 'flex', alignItems: 'center',
          justifyContent: { xs: 'space-between', md: 'center' }, gap: { xs: 2, md: 5 },
        }}>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1 }}>
              {items.length} ítem{items.length !== 1 ? 's' : ''} · {unidades} u.{pesoTotal > 0 ? ` · ${pesoTotal.toFixed(3)} kg` : ''}
            </Typography>
            {descuentoMonto > 0 ? (
              <>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.3 }}>
                  Subtotal {fmtMoneda(subtotalBruto)} · Descuento -{fmtMoneda(descuentoMonto)}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.1 }}>{fmtMoneda(total)}</Typography>
              </>
            ) : (
              <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.1 }}>{fmtMoneda(total)}</Typography>
            )}
          </Box>
          <Button variant="contained" color="primary" size="large" onClick={guardar}
            disabled={submitting || items.length === 0}
            startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <Save />}>
            {submitting ? 'Registrando…' : 'Registrar'}
          </Button>
        </Container>
      </Paper>
    </Container>
  );
}

export default RegistroProducto;
